import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import type { BoxProps } from '@/components/Box';
import type { FlexProps } from '@/components/Flex';
import { Flex } from '@/components/Flex';
import * as S from '@/components/StickyLayout.styles';
import type { WithChildren } from '@/lib/types';
import { useConfigProvider } from '@/providers/ConfigProvider';

export type StickyLayoutProps = WithChildren &
  Pick<FlexProps, 'backgroundColor'> & {
    style?: CSSProperties;
  };

const StickyLayout = ({ children, style, ...props }: StickyLayoutProps) => (
  <Flex
    data-test-id='StickyLayout'
    flexDirection='column'
    justifyContent='space-between'
    style={{ minHeight: '100vh', maxWidth: '100vw', ...style }}
    {...props}
  >
    {children}
  </Flex>
);

type MainProps = WithChildren &
  Pick<BoxProps, 'backgroundColor'> & { headerBackground?: string };

// Must run inline while the HTML is parsed, before <main> paints: the header height comes from CMS
// content, and next/script would only run after hydration, when the margin change shifts the page.
const setHeaderOffsetScript = `(function(){var h=document.querySelector('[data-testid="HeaderWrapper"]');if(h)document.documentElement.style.setProperty('${S.HEADER_OFFSET_VAR}',h.offsetHeight-1+'px')})()`;

const Main = ({ children, ...props }: MainProps) => {
  const { headerRef } = useConfigProvider();
  const [headerHeight, setHeaderHeight] = useState(0);
  const isTransparentHeader = !!props.headerBackground
    ?.toLowerCase()
    ?.includes('transparent');

  useEffect(() => {
    if (isTransparentHeader) {
      setHeaderHeight(0);
      return;
    }
    if (!headerRef?.current) return;

    const observer = new ResizeObserver(() => {
      setHeaderHeight(headerRef.current?.offsetHeight || 0);
    });

    observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, [headerRef]);

  return (
    <>
      {!isTransparentHeader && (
        <script dangerouslySetInnerHTML={{ __html: setHeaderOffsetScript }} />
      )}
      <S.MainBox
        $headerHeight={headerHeight}
        $useHeaderOffset={!isTransparentHeader}
        as='main'
        data-test-id='StickyLayoutMain'
        id='main-content'
        {...props}
        style={{ flex: 1 }}
      >
        {children}
      </S.MainBox>
    </>
  );
};

StickyLayout.Main = Main;

export { StickyLayout };
